import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function CheckerAddEdit() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        nickname: "",
        per_product_amount: "",
        payment_type: "per_product",
        payment_value: "",
        settlement_type: "receivable",
        opening_balance: "",
        status: "1", // Default to Active ("1")
        password: "",
        password_confirmation: "",
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [pageLoading, setPageLoading] = useState(isEdit && !location.state?.checker);
    const [alert, setAlert] = useState(null);

    const getAuthHeader = () => ({
        headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
    });

    const populateForm = (item) => {
        const rawStatus = item.status ?? item.account_status;
        setFormData({
            name: item.name || "",
            email: item.email || "",
            nickname: item.nickname || "",
            per_product_amount: item.per_product_amount || "",
            payment_type: item.payment_type || "per_product",
            payment_value: item.payment_value || "",
            settlement_type: item.settlement_type || "receivable",
            opening_balance: item.opening_balance ?? "",
            status: rawStatus !== undefined && rawStatus !== null ? String(rawStatus) : "1",
            password: "",
            password_confirmation: "",
        });
    };

    useEffect(() => {
        if (isEdit) {
            if (location.state?.checker) {
                populateForm(location.state.checker);
            } else {
                const fetchChecker = async () => {
                    try {
                        const res = await axios.get(
                            `${process.env.REACT_APP_API_URL}/admin/checker`,
                            getAuthHeader()
                        );
                        if (res.data?.success) {
                            const list = Array.isArray(res.data.data) ? res.data.data : [];
                            const item = list.find((c) => c.id === parseInt(id));
                            if (item) {
                                populateForm(item);
                            } else {
                                setAlert({ type: "danger", message: "Checker not found." });
                            }
                        }
                    } catch (err) {
                        setAlert({ type: "danger", message: "Failed to load checker details." });
                    } finally {
                        setPageLoading(false);
                    }
                };
                fetchChecker();
            }
        }
    }, [id, isEdit, location.state]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});
        setAlert(null);

        // Build payload ensuring status is sent properly as an integer (1 or 0)
        const payload = {
            ...formData,
            status: parseInt(formData.status, 10),
        };

        // Remove empty password fields during edit if left blank
        if (isEdit && !payload.password) {
            delete payload.password;
            delete payload.password_confirmation;
        }

        const url = isEdit
            ? `${process.env.REACT_APP_API_URL}/admin/checker/${id}`
            : `${process.env.REACT_APP_API_URL}/admin/checker`;

        try {
            const response = await axios.post(url, payload, getAuthHeader());

            if (response.data?.success) {
                navigate("/checkers");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setAlert({
                    type: "danger",
                    message: err.response?.data?.message || "Operation failed. Please try again.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (pageLoading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("Loading checker data...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4 p-4 rounded-4 shadow-sm border-0 bg-white d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div>
                            <h3 className="mc-breadcrumb-title fw-bold text-dark m-0">
                                {isEdit ? t("Edit Checker") : t("Add Checker")}
                            </h3>
                            <ul className="mc-breadcrumb-list mt-1 m-0">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link text-secondary">
                                        {t("Home")}
                                    </Link>
                                </li>
                                <li className="mc-breadcrumb-item">
                                    <Link to="/checkers" className="mc-breadcrumb-link text-secondary">
                                        {t("Checkers")}
                                    </Link>
                                </li>
                                <li className="mc-breadcrumb-item text-primary fw-semibold">
                                    {isEdit ? t("Edit") : t("Add")}
                                </li>
                            </ul>
                        </div>
                        <Button variant="secondary" className="rounded-3 px-4" onClick={() => navigate("/checkers")}>
                            {t("Back")}
                        </Button>
                    </div>
                </Col>

                <Col xl={10} className="mx-auto">
                    <Card className="border-0 shadow-sm rounded-4">
                        <Card.Body className="p-4">
                            {alert && (
                                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)} className="rounded-3 shadow-sm">
                                    {alert.message}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Row className="g-3">
                                    {/* Row 1 */}
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Name <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                isInvalid={!!errors.name}
                                                placeholder="e.g. John Doe"
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.name?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Nickname <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="nickname"
                                                value={formData.nickname}
                                                onChange={handleChange}
                                                isInvalid={!!errors.nickname}
                                                placeholder="e.g. Johnny"
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.nickname?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Email <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                isInvalid={!!errors.email}
                                                placeholder="e.g. checker@example.com"
                                                required
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.email?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {/* Row 2 */}
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Payment Type</Form.Label>
                                            <Form.Select
                                                name="payment_type"
                                                value={formData.payment_type}
                                                onChange={handleChange}
                                                isInvalid={!!errors.payment_type}
                                            >
                                                <option value="">Select Payment Type</option>
                                                <option value="salary">Salary</option>
                                                <option value="per_product">Per Product</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.payment_type?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Payment Value</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                name="payment_value"
                                                value={formData.payment_value}
                                                onChange={handleChange}
                                                isInvalid={!!errors.payment_value}
                                                placeholder="e.g. 10"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.payment_value?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Settlement Type</Form.Label>
                                            <Form.Select
                                                name="settlement_type"
                                                value={formData.settlement_type}
                                                onChange={handleChange}
                                                isInvalid={!!errors.settlement_type}
                                            >
                                                <option value="">Select Settlement Type</option>
                                                <option value="receivable">Receivable</option>
                                                <option value="payable">Payable</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.settlement_type?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {/* Row 3 */}
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">Opening Balance</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="opening_balance"
                                                value={formData.opening_balance}
                                                onChange={handleChange}
                                                isInvalid={!!errors.opening_balance}
                                                placeholder="e.g. 0.00"
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.opening_balance?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                Status <span className="text-danger">*</span>
                                            </Form.Label>
                                            <Form.Select
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                                isInvalid={!!errors.status}
                                                required
                                            >
                                                <option value="1">Active</option>
                                                <option value="0">Inactive</option>
                                            </Form.Select>
                                            <Form.Control.Feedback type="invalid">
                                                {errors.status?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label className="fw-semibold">
                                                {isEdit ? "New Password (Optional)" : <>Password <span className="text-danger">*</span></>}
                                            </Form.Label>
                                            <Form.Control
                                                type="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                isInvalid={!!errors.password}
                                                placeholder={
                                                    isEdit
                                                        ? "Leave empty to keep current"
                                                        : "Min. 8 characters"
                                                }
                                                required={!isEdit}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {errors.password?.[0]}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </Col>

                                    {/* Row 4 (Password Confirmation) */}
                                    {(formData.password || !isEdit) && (
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">
                                                    Confirm Password <span className="text-danger">*</span>
                                                </Form.Label>
                                                <Form.Control
                                                    type="password"
                                                    name="password_confirmation"
                                                    value={formData.password_confirmation}
                                                    onChange={handleChange}
                                                    placeholder="Re-enter password"
                                                    required={!isEdit || Boolean(formData.password)}
                                                />
                                            </Form.Group>
                                        </Col>
                                    )}
                                </Row>

                                <div className="d-flex gap-2 justify-content-end mt-4 pt-3 border-top">
                                    <Button
                                        variant="secondary"
                                        className="px-4 border"
                                        onClick={() => navigate("/checkers")}
                                    >
                                        Cancel
                                    </Button>
                                    <Button variant="primary" type="submit" className="px-4" disabled={submitting}>
                                        {submitting
                                            ? isEdit
                                                ? "Updating..."
                                                : "Saving..."
                                            : isEdit
                                                ? "Update Checker"
                                                : "Save Checker"}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}